import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useGetMany, useGetManyReference } from 'react-admin';
import { caseContacts } from '../funerals/caseContacts';
import { canBeFruitionPrimary } from '../funerals/deceasedPrimary';

/**
 * Who becomes the main contact at fruition (T-210). Sent to the backend as is:
 * `contract/fulfill-preneed` and `contract/fruition-primary` take these fields.
 */
export type FruitionPrimaryChoice =
  | { newPrimaryClientId: number; newPrimaryRelationship?: string }
  | {
      newPrimaryContact: {
        firstName: string;
        lastName: string;
        relationship: string;
        phone?: string;
        email?: string;
      };
    };

type NewContact = { firstName: string; lastName: string; relationship: string; phone: string; email: string };

const EMPTY: NewContact = { firstName: '', lastName: '', relationship: '', phone: '', email: '' };

const NEW = 'new';

/** The choice, or null while it's incomplete (Convert stays disabled). */
export function fruitionChoiceFrom(selected: string, contact: NewContact): FruitionPrimaryChoice | null {
  if (!selected) return null;
  if (selected !== NEW) return { newPrimaryClientId: Number(selected) };
  const c = {
    firstName: contact.firstName.trim(),
    lastName: contact.lastName.trim(),
    relationship: contact.relationship.trim(),
    phone: contact.phone.trim(),
    email: contact.email.trim(),
  };
  if (!c.firstName || !c.lastName || !c.relationship) return null;
  if (!c.phone && !c.email) return null;
  if (c.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email)) return null;
  return {
    newPrimaryContact: {
      firstName: c.firstName,
      lastName: c.lastName,
      relationship: c.relationship,
      ...(c.phone ? { phone: c.phone } : {}),
      ...(c.email ? { email: c.email } : {}),
    },
  };
}

const clientName = (c: any) =>
  c?.name || `${c?.first_name ?? ''} ${c?.last_name ?? ''}`.replace(/\s+/g, ' ').trim() || `Client #${c?.id}`;

type Props = {
  funeral: any;
  onChange: (choice: FruitionPrimaryChoice | null) => void;
};

/**
 * Required at fruition: the planner has died, so family emails, texts and
 * portal links need someone else. Lists everyone on the case but the deceased
 * (with their relationship), or adds a new person.
 */
const FruitionPrimaryPicker = ({ funeral, onChange }: Props) => {
  const [selected, setSelected] = useState('');
  const [contact, setContact] = useState<NewContact>(EMPTY);

  const { data: links } = useGetManyReference(
    'funeral_clients',
    { target: 'funeral_id', id: funeral?.id },
    { enabled: !!funeral?.id },
  );

  const candidates = useMemo(
    () =>
      caseContacts({
        primaryClientId: funeral?.primary_client_id,
        payingClientId: funeral?.paying_client_id,
        authorizedClientId: funeral?.authorized_client_id,
        declarantClientId: funeral?.declarant_client_id,
        deceasedClientId: funeral?.deceased_client_id,
        linked: links as any,
        primaryRelationship: funeral?.primary_client_relationship,
        payingRelationship: funeral?.paying_client_relationship,
        authorizedRelationship: funeral?.authorized_client_relationship,
      }).filter((c) => canBeFruitionPrimary(c.clientId, funeral)),
    [funeral, links],
  );

  const { data: clients } = useGetMany(
    'clients',
    { ids: candidates.map((c) => c.clientId) },
    { enabled: candidates.length > 0 },
  );
  const clientById = useMemo(
    () => new Map((clients ?? []).map((c: any) => [Number(c.id), c])),
    [clients],
  );

  useEffect(() => {
    onChange(fruitionChoiceFrom(selected, contact));
  }, [selected, contact, onChange]);

  const set = (field: keyof NewContact) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setContact((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        New primary contact
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        The person who planned ahead has died. Choose who receives family emails, texts and links from
        now on. Required.
      </Typography>
      <RadioGroup value={selected} onChange={(e) => setSelected(e.target.value)}>
        {candidates.map((c) => {
          const client = clientById.get(c.clientId);
          const details = [client?.phone_number, client?.email].filter(Boolean).join(' · ');
          return (
            <FormControlLabel
              key={c.clientId}
              value={String(c.clientId)}
              control={<Radio size="small" />}
              label={
                <span>
                  <strong>{clientName(client ?? { id: c.clientId })}</strong>
                  {' — '}
                  {c.relationship || 'relationship not set'}
                  {details && (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' · '}
                      {details}
                    </Typography>
                  )}
                </span>
              }
            />
          );
        })}
        <FormControlLabel value={NEW} control={<Radio size="small" />} label="Add someone new" />
      </RadioGroup>
      {candidates.length === 0 && (
        <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
          No family contact is on this case yet. Add one.
        </Typography>
      )}
      {selected === NEW && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
          <TextField label="First name" size="small" required value={contact.firstName} onChange={set('firstName')} />
          <TextField label="Last name" size="small" required value={contact.lastName} onChange={set('lastName')} />
          <TextField
            label="Relationship to the deceased"
            size="small"
            required
            value={contact.relationship}
            onChange={set('relationship')}
          />
          <TextField label="Phone" size="small" value={contact.phone} onChange={set('phone')} />
          <TextField label="Email" size="small" type="email" value={contact.email} onChange={set('email')} />
          <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
            A phone number or an email is required.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default FruitionPrimaryPicker;
